import apiClient from './api';
import {
  AuthRequest,
  AuthResponse,
  UserAccount,
  RegisterRequest,
  PasswordChangeRequest,
  PasswordResetRequest,
  PasswordResetFinishRequest,
  IAuthService,
} from '@/types/api';
import { clearGeoServerTokenCookie, syncGeoServerTokenCookie } from '@/utils/geoserverAccess';

class AuthService implements IAuthService {
  async login(username: string, password: string): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>('/authenticate', {
        username,
        password,
      } as AuthRequest);

      if (response.data.id_token || response.data.access_token) {
        const token = response.data.id_token || response.data.access_token;
        if (token) {
          localStorage.setItem('auth_token', token);
        }
        if (response.data.expires_in) {
          const expiresAt = Date.now() + response.data.expires_in * 1000;
          localStorage.setItem('token_expires_at', expiresAt.toString());
        } else {
          localStorage.removeItem('token_expires_at');
        }
        syncGeoServerTokenCookie();
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAccount(): Promise<UserAccount> {
    try {
      const response = await apiClient.get<UserAccount>('/account');
      localStorage.setItem('user_account', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  logout(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_account');
    localStorage.removeItem('token_expires_at');
    clearGeoServerTokenCookie();
  }

  async register(request: RegisterRequest): Promise<UserAccount> {
    try {
      const response = await apiClient.post<UserAccount>('/register', request);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async changePassword(request: PasswordChangeRequest): Promise<void> {
    try {
      await apiClient.post('/account/change-password', request);
    } catch (error) {
      throw error;
    }
  }

  async resetPassword(request: PasswordResetRequest): Promise<void> {
    try {
      await apiClient.post('/account/reset-password/init', request);
    } catch (error) {
      throw error;
    }
  }

  async resetPasswordFinish(request: PasswordResetFinishRequest): Promise<void> {
    try {
      await apiClient.post('/account/reset-password/finish', request);
    } catch (error) {
      throw error;
    }
  }
}

export const authService = new AuthService();
export default authService;
